import { useEffect, useState } from 'react'
import { AlertTriangle, X } from 'lucide-react'
import ToggleField from '@/components/Indicators/ToggleField'
import {
  getTelegramNotificationsApi,
  loadLocalSubscriptions,
  saveLocalSubscriptions,
  resolveTelegramSubscriptions,
  TF_LABELS,
  tfsFor,
  toCanonicalSymbol,
  isCellEnabled,
  toggleCell,
  setSymbolEnabled,
  listSymbols,
} from '@/services/telegramNotifications'

// Sección reutilizable de suscripciones Telegram por usuario × indicador ×
// symbol × timeframe. Se monta dentro de una <Section> del panel de
// configuración de cualquier indicador soportado (basta pasar su indicatorId).
// Si la página no ha inyectado el transporte (initTelegramNotificationsApi) —
// p.ej. la app privada, que aún no tiene backend de suscripciones — no
// renderiza nada.
//
// El toggle contextual captura el activo y timeframe ACTUALES del chart
// (props symbol/timeframe) y conmuta exactamente esa combinación; el listado
// agrupa por activo y permite activar/desactivar cada combinación.
//
// `fixedTimeframe` (opcional): para indicadores cuyo motor de BACKEND solo
// evalúa un timeframe fijo (rango_quantum: 4H únicamente, ver
// rango_quantum_signal_tasks.py::TIMEFRAME) independientemente de qué TF esté
// viendo el usuario en el chart — el toggle contextual se suscribe SIEMPRE a
// ese TF fijo (no al `timeframe` visible) y su label lo dice explícitamente
// ("Señales de X — motor 4H") para no sugerir que el TF visible determina el
// TF de la señal. Encontrado 2026-08-14 (validación en pantalla de Marc, P2).
export default function TelegramNotificationsSection({ indicatorId, symbol, timeframe, fixedTimeframe }) {
  const api = getTelegramNotificationsApi()
  // Sin transporte: inerte (ni siquiera pintamos el primer estado local).
  const [subscriptions, setSubscriptions] = useState(() => loadLocalSubscriptions())
  const [linked, setLinked] = useState(null) // null = cargando
  const [loading, setLoading] = useState(!!api)
  const [errorMessage, setErrorMessage] = useState(null)

  useEffect(() => {
    if (!api) return
    let cancelled = false
    Promise.all([api.getStatus(), api.getSubscriptions()])
      .then(([status, remote]) => {
        if (cancelled) return
        setLinked(status?.linked === true)
        const resolved = resolveTelegramSubscriptions(remote?.subscriptions)
        setSubscriptions(resolved)
        saveLocalSubscriptions(resolved) // el remoto gana y refresca localStorage
      })
      .catch(() => { if (!cancelled) setLinked(null) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [api])

  // Tras abrir el deep-link de vinculación, re-consulta el estado al volver
  // el foco a la ventana: así aparece el toggle sin recargar la página.
  useEffect(() => {
    if (!api || linked !== false) return
    let cancelled = false
    const onFocus = () => {
      api.getStatus()
        .then((status) => { if (!cancelled) setLinked(status?.linked === true) })
        .catch(() => {})
    }
    window.addEventListener('focus', onFocus)
    return () => { cancelled = true; window.removeEventListener('focus', onFocus) }
  }, [api, linked])

  if (!api) return null

  // Estado local optimista + localStorage inmediato. A diferencia del resto
  // de config de indicador (fire-and-forget tolerado), un PUT de
  // suscripciones que falla en silencio es peor aquí: el usuario cree que
  // activó notificaciones que en realidad el servidor rechazó (2026-08-14,
  // P2 — 400 repetido en Network sin ningún indicio en la UI). Si falla: se
  // revierte el toggle al estado anterior (no mentir con un check activado
  // que el servidor no aceptó) y se muestra el motivo explícito (regla B.5,
  // origen en Parte J).
  const persist = (next, previous) => {
    const resolved = resolveTelegramSubscriptions(next)
    setSubscriptions(resolved)
    saveLocalSubscriptions(resolved)
    setErrorMessage(null)
    api.putSubscriptions(resolved)
      .catch((err) => {
        const reverted = resolveTelegramSubscriptions(previous)
        setSubscriptions(reverted)
        saveLocalSubscriptions(reverted)
        if (linked === false) {
          setErrorMessage('Vincula tu Telegram primero para activar notificaciones.')
          return
        }
        // El transporte real (publicApi.js) usa fetch plano, no axios: el
        // error trae `err.body` (JSON parseado de la respuesta), NO
        // `err.response.data` (eso es axios). Se comprueban ambas formas
        // por si el transporte cambia — nunca asumir axios sin verificar.
        const detail = err?.body?.detail ?? err?.response?.data?.detail
        setErrorMessage(
          typeof detail === 'string' && detail
            ? detail
            : 'No se pudo guardar la suscripción. Inténtalo de nuevo.',
        )
      })
  }

  const connectTelegram = () => {
    api.createLinkToken()
      .then((res) => {
        if (res?.link) window.open(res.link, '_blank', 'noopener')
      })
      .catch(() => {})
  }

  if (loading) {
    return <span className="text-[10px] text-[var(--ink-soft,#4A5A52)]">Cargando notificaciones…</span>
  }

  if (linked === false) {
    return (
      <div className="space-y-1.5">
        <p className="text-[11px] text-[var(--ink-soft,#4A5A52)]">
          Vincula tu cuenta de Telegram para recibir señales de este indicador.
        </p>
        <button
          onClick={connectTelegram}
          className="w-full py-1.5 text-[11px] font-medium rounded border border-[var(--hair,rgba(176,141,87,0.35))] bg-[var(--paper,#FDFDFA)] text-[var(--green-deep,#0B3D2E)] hover:bg-[var(--gold,#B08D57)] hover:text-white transition-colors"
        >
          Conectar mi Telegram
        </button>
      </div>
    )
  }

  if (linked !== true) {
    // Carga fallida (p.ej. sin red): nada accionable, dejamos la sección vacía.
    return <span className="text-[10px] text-[var(--ink-soft,#4A5A52)]">No se pudo cargar el estado de Telegram.</span>
  }

  // Combinación del chart actual (props del panel). Sin ellas no hay toggle
  // contextual, solo el listado. `fixedTimeframe` (rango_quantum: 4H) manda
  // sobre el TF visible del chart — el motor de backend no evalúa otro.
  const currentSymbol = toCanonicalSymbol(symbol || '')
  const indicatorTfs = tfsFor(indicatorId)
  const effectiveTf = fixedTimeframe && indicatorTfs.includes(fixedTimeframe) ? fixedTimeframe : null
  const currentTf = effectiveTf || (indicatorTfs.includes(timeframe) ? timeframe : null)
  const hasCurrent = !!currentSymbol && !!currentTf
  const symbols = listSymbols(subscriptions, indicatorId)

  const errorBanner = errorMessage && (
    <div
      className="flex items-start gap-1.5 rounded border px-2 py-1.5 text-[10px]"
      style={{ background: 'rgba(183,28,28,0.08)', borderColor: 'rgba(183,28,28,0.35)', color: '#B71C1C' }}
      role="alert"
    >
      <AlertTriangle size={12} className="mt-0.5 shrink-0" />
      <span className="flex-1">{errorMessage}</span>
      <button onClick={() => setErrorMessage(null)} className="shrink-0 opacity-70 hover:opacity-100">
        <X size={11} />
      </button>
    </div>
  )

  const tfChip = (sym, tf) => {
    const active = isCellEnabled(subscriptions, indicatorId, sym, tf)
    return (
      <button
        key={tf}
        type="button"
        onClick={() => persist(toggleCell(subscriptions, indicatorId, sym, tf), subscriptions)}
        aria-pressed={active}
        data-symbol={sym}
        data-tf={tf}
        className={`px-1 py-1 text-[10px] font-medium rounded border transition-colors ${
          active
            ? 'bg-[var(--paper,#FDFDFA)] border-[var(--gold,#B08D57)] text-[var(--green-deep,#0B3D2E)]'
            : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-400 hover:bg-slate-50 dark:hover:bg-gray-700'
        }`}
      >
        {TF_LABELS[tf]}
      </button>
    )
  }

  return (
    <div className="space-y-2">
      {errorBanner}

      {hasCurrent && (
        <ToggleField
          label={
            effectiveTf
              ? `Señales de ${currentSymbol} — motor ${TF_LABELS[effectiveTf]}`
              : `Señales de ${currentSymbol} en ${TF_LABELS[currentTf]}`
          }
          value={isCellEnabled(subscriptions, indicatorId, currentSymbol, currentTf)}
          onChange={() => persist(toggleCell(subscriptions, indicatorId, currentSymbol, currentTf), subscriptions)}
          data-testid="telegram-toggle"
        />
      )}

      {symbols.length === 0 ? (
        <p className="text-[10px] text-[var(--ink-soft,#4A5A52)]">
          Sin activos suscritos — activa el actual arriba.
        </p>
      ) : (
        <div className="space-y-2" data-testid="telegram-subscriptions-list">
          {symbols.map((sym) => {
            const conf = subscriptions[indicatorId][sym]
            return (
              <div key={sym} className={`space-y-1 ${conf.enabled ? '' : 'opacity-60'}`} data-symbol-row={sym}>
                <ToggleField
                  label={sym}
                  value={conf.enabled}
                  onChange={(enabled) => persist(setSymbolEnabled(subscriptions, indicatorId, sym, enabled), subscriptions)}
                />
                {/* Con TF fijo (motor de backend de TF único) no tiene sentido
                    ofrecer chips de otros TFs que nunca van a disparar. */}
                {!effectiveTf && (
                  <div className={`grid grid-cols-4 gap-1 ${conf.enabled ? '' : 'opacity-50 pointer-events-none'}`}>
                    {indicatorTfs.map((tf) => tfChip(sym, tf))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
